import { useEffect, useRef, useState } from "react";
import { Camera, CameraOff, ScanLine, Search, Loader2, ChevronLeft } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { fileUrlToFull } from "@/components/ImageUpload";
import { formatDateTime, formatTime } from "@/lib/format";
import { toast } from "sonner";

const STATUS_BADGE = {
  draft: { label: "مسودة", cls: "bg-muted text-muted-foreground" },
  submitted: { label: "مقدمة", cls: "bg-info/10 text-info" },
  approved: { label: "معتمدة", cls: "bg-success/10 text-success" },
  rejected: { label: "مرفوضة", cls: "bg-destructive/10 text-destructive" },
};

export default function ScannerPage() {
  const [manualCode, setManualCode] = useState("");
  const [result, setResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const scannerRef = useRef(null);
  const elementId = "qr-camera-area";
  const lookupGuard = useRef(false);

  const loadLogs = async () => {
    try {
      const { data } = await api.get("/scanner/logs");
      setLogs(data);
    } catch {}
  };
  useEffect(() => { loadLogs(); }, []);

  const lookup = async (code) => {
    if (!code || lookupGuard.current) return;
    lookupGuard.current = true;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/scanner/lookup", { form_number: code.trim() });
      setResult(data);
      toast.success(`تم العثور على ${data.form_number}`);
      loadLogs();
    } catch (e) { toast.error(formatApiError(e)); }
    finally {
      setLoading(false);
      setTimeout(() => { lookupGuard.current = false; }, 800);
    }
  };

  const startCamera = async () => {
    try {
      if (!scannerRef.current) {
        scannerRef.current = new Html5Qrcode(elementId, { verbose: false });
      }
      await scannerRef.current.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: 240 },
        async (decoded) => {
          await lookup(decoded);
        },
        () => {}
      );
      setCameraOn(true);
    } catch (e) {
      toast.error("تعذّر تشغيل الكاميرا. تأكّد من السماح بالوصول.");
    }
  };

  const stopCamera = async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      }
    } catch {}
    setCameraOn(false);
  };

  useEffect(() => () => { stopCamera(); /* eslint-disable-next-line */ }, []);

  return (
    <div data-testid="scanner-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold tracking-tight">ماسح QR</h1>
        <p className="text-muted-foreground mt-1">امسح رقم الاستمارة عبر الكاميرا أو ماسح خارجي أو إدخال يدوي</p>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Scanner */}
        <div className="col-span-12 lg:col-span-5">
          <div className="gov-card p-5 mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-lg font-semibold">إدخال يدوي / ماسح خارجي</div>
              <ScanLine className="w-5 h-5 text-muted-foreground" />
            </div>
            <form onSubmit={(e) => { e.preventDefault(); lookup(manualCode); setManualCode(""); }} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute top-1/2 -translate-y-1/2 right-3 w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid="scanner-manual-input"
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  placeholder="PS-2026-000001"
                  className="ps-3 pe-10 h-11 font-mono-num"
                  autoFocus
                />
              </div>
              <Button type="submit" disabled={loading} data-testid="scanner-lookup-button" className="bg-primary hover:bg-primary/90">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "بحث"}
              </Button>
            </form>
            <p className="text-xs text-muted-foreground mt-2">
              الماسحات الخارجية تكتب الرقم تلقائياً ثم تضغط Enter.
            </p>
          </div>

          <div className="gov-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-lg font-semibold">كاميرا المتصفح</div>
              <Button
                variant="outline"
                onClick={cameraOn ? stopCamera : startCamera}
                data-testid="scanner-camera-toggle"
              >
                {cameraOn ? <><CameraOff className="w-4 h-4 ms-1" /> إيقاف</> : <><Camera className="w-4 h-4 ms-1" /> تشغيل</>}
              </Button>
            </div>
            <div id={elementId} className="rounded-sm overflow-hidden bg-surface min-h-[260px] flex items-center justify-center text-muted-foreground">
              {!cameraOn && <div className="text-sm">اضغط "تشغيل" لبدء المسح</div>}
            </div>
          </div>
        </div>

        {/* Result */}
        <div className="col-span-12 lg:col-span-7">
          {result ? (
            <div className="gov-card overflow-hidden animate-slide-up" data-testid="scanner-result">
              <div className="px-5 py-4 border-b border-border bg-surface flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">رقم الاستمارة</div>
                  <div className="font-mono-num font-bold text-2xl text-primary">{result.form_number}</div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${(STATUS_BADGE[result.status] || STATUS_BADGE.submitted).cls}`}>
                  {(STATUS_BADGE[result.status] || STATUS_BADGE.submitted).label}
                </span>
              </div>
              <div className="p-5 space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-muted-foreground">النموذج</div>
                    <div className="font-medium">{result.template?.title}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">المُقدِّم</div>
                    <div className="font-medium">{result.submitted_by_name}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">تاريخ التقديم</div>
                    <div className="font-mono text-xs">{formatDateTime(result.submitted_at)}</div>
                  </div>
                </div>
                <div className="border-t border-border pt-3">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">بيانات الاستمارة</div>
                  <div className="space-y-2">
                    {(result.template?.fields || []).map((f) => {
                      const v = result.values?.[f.id];
                      const isFileObj = v && typeof v === "object" && v.url;
                      const isImage = isFileObj && v.type?.startsWith("image/");
                      return (
                        <div key={f.id} className="flex items-start gap-3 text-sm">
                          <div className="w-1/3 text-muted-foreground">{f.label}</div>
                          <div className="flex-1 font-medium break-words">
                            {isImage ? (
                              <img src={fileUrlToFull(v.url)} alt={f.label} className="max-h-32 rounded-sm border border-border" />
                            ) : isFileObj ? (
                              <a href={fileUrlToFull(v.url)} target="_blank" rel="noreferrer" className="text-primary underline">{v.name}</a>
                            ) : Array.isArray(v) ? v.join("، ") : (v || <span className="text-muted-foreground">—</span>)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="gov-card p-12 text-center text-muted-foreground">
              <ScanLine className="w-12 h-12 mx-auto mb-3 opacity-30" />
              امسح أو أدخل رقم الاستمارة لعرض البيانات
            </div>
          )}

          <div className="gov-card mt-4 overflow-hidden">
            <div className="px-5 py-3 border-b border-border bg-surface text-xs uppercase tracking-wider text-muted-foreground font-medium">
              سجل المسح الأخير
            </div>
            <div className="divide-y divide-border/60 max-h-[260px] overflow-y-auto">
              {logs.slice(0, 8).map((log) => (
                <div key={log.id} className="px-5 py-2.5 flex items-center justify-between text-sm">
                  <div className="flex items-center gap-3">
                    <ChevronLeft className="w-3 h-3 text-muted-foreground" />
                    <div className="font-mono-num font-medium">{log.form_number}</div>
                    <div className="text-xs text-muted-foreground">— {log.scanned_by_name}</div>
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">
                    {formatTime(log.scanned_at)}
                  </div>
                </div>
              ))}
              {!logs.length && <div className="px-5 py-8 text-center text-sm text-muted-foreground">لا يوجد سجل بعد</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
